import type { SagaIterator } from 'redux-saga';
import { put, select, take, takeEvery, takeLatest } from 'redux-saga/effects';

import * as businessActions from 'app-components/flux/business/businessActions';
import { resourceSagaUtils, resourceStoreTypes } from 'app-components/flux/core';
import type { ResourceActionCreator, ResourceFailureActionCreator } from 'app-components/flux/core/resourceStoreTypes';
import { isConfigModifiedFromParent } from 'app-components/flux/report-configurations/defaultReportConfigurations';
import * as reportConfigurationsActions from 'app-components/flux/report-configurations/reportConfigurationsActions';
import * as reportConfigurationsSelectors from 'app-components/flux/report-configurations/reportConfigurationsSelectors';
import * as reportConfigurationsTransformers from 'app-components/flux/report-configurations/reportConfigurationsTransformers';
import type {
  ReportConfiguration,
  ReportConfigurationProps,
} from 'app-components/flux/report-configurations/reportConfigurationsTypes';
import { mkReportConfigFilters } from 'app-components/flux/report-configurations/reportConfigurationsTypes';

import * as actions from './reportConfigurationDefaultsActions';
import * as selectors from './reportConfigurationDefaultsSelectors';

const mkQcsSyncReportConfigurationDefaultsConfig =
  resourceStoreTypes.createTypedQcsSyncResourcesConfigFactory<ReportConfigurationProps>();

type BusinessChangeAction = { type: string; meta?: { context?: string } };
type ReportConfigurationsById = ReturnType<typeof reportConfigurationsSelectors.getResourcesById>;
type ReportConfigurationDefaultsById = ReturnType<typeof selectors.getResourcesById>;

const mkResourceConfig = (
  successAction: ResourceActionCreator<ReportConfigurationProps>,
  failureAction: ResourceFailureActionCreator,
) =>
  mkQcsSyncReportConfigurationDefaultsConfig({
    resourceName: 'report-configuration/defaults',
    resourceBaseUrl: 'reports/report-configuration/defaults',
    axiosConfig: { params: { includeHidden: true } },
    applyResourceFromCRUDResponse: false,
    transformResponseToResources: reportConfigurationsTransformers.transformResponseToReportConfigurations,
    successAction,
    failureAction,
  });

export function* getReportConfigurationDefaultsActionWatcher(): SagaIterator {
  const resourceConfig = mkResourceConfig(
    actions.getReportConfigurationDefaultsSuccess,
    actions.getReportConfigurationDefaultsFailure,
  );
  yield takeLatest(
    actions.getReportConfigurationDefaults.type,
    resourceSagaUtils.qcsSyncGetResources<ReportConfigurationProps>,
    resourceConfig,
  );
}

export function* refreshReportConfigurationDefaultsAfterBusinessChange(action?: BusinessChangeAction): SagaIterator {
  if (action?.type === businessActions.getBusinessSuccess.type && action.meta?.context !== 'BUMP_CHANGE_NUDGE') return;

  const configsBefore: ReportConfigurationsById = yield select(reportConfigurationsSelectors.getResourcesById);
  const defaultsBefore: ReportConfigurationDefaultsById = yield select(selectors.getResourcesById);
  const untouchedConfigs = configsBefore
    .valueSeq()
    .filter((config) => {
      if (
        !config.parentConfigId ||
        config.parentPersistentType !== 'PREDEFINED' ||
        (config.persistentType !== 'OPEN' && config.persistentType !== 'TEMPORARY') ||
        config.reportSubType === 'SAVINGS' ||
        config.reportSubType === 'NET_WORTH'
      ) {
        return false;
      }
      const parent = defaultsBefore.get(config.parentConfigId);
      return Boolean(parent && !isConfigModifiedFromParent(config, configsBefore, defaultsBefore));
    })
    .toArray();

  yield put(actions.getReportConfigurationDefaults());

  const refreshResult: { type: string } = yield take([
    actions.getReportConfigurationDefaultsSuccess.type,
    actions.getReportConfigurationDefaultsFailure.type,
  ]);
  if (refreshResult.type === actions.getReportConfigurationDefaultsFailure.type || untouchedConfigs.length === 0)
    return;

  const defaultsAfter: ReportConfigurationDefaultsById = yield select(selectors.getResourcesById);
  const configsAfter: ReportConfigurationsById = yield select(reportConfigurationsSelectors.getResourcesById);
  const rebasedConfigs = untouchedConfigs.flatMap((snapshot): ReportConfiguration[] => {
    if (!snapshot.id || !snapshot.parentConfigId || configsAfter.get(snapshot.id) !== snapshot) return [];
    const parent = defaultsAfter.get(snapshot.parentConfigId);
    if (!parent) return [];

    const filter = parent.filter
      ? mkReportConfigFilters({
          type: parent.filter.type,
          filterItems: parent.filter.filterItems,
          id: snapshot.filter?.id,
          clientId: snapshot.filter?.clientId,
          parentIds: snapshot.filter?.parentIds,
          isDeleted: snapshot.filter?.isDeleted,
        })
      : parent.filter;
    return [snapshot.set('filter', filter)];
  });

  if (rebasedConfigs.length > 0) {
    yield put(
      reportConfigurationsActions.getReportConfigurationsSuccess(
        { resources: rebasedConfigs, doMerge: false },
        { context: 'BUSINESS_CHANGE_DEFAULT_REBASE' },
      ),
    );
  }
}

export function* businessChangeReportConfigurationDefaultsWatcher(): SagaIterator {
  yield takeEvery(
    [
      businessActions.createBusinessSuccess.type,
      businessActions.deleteBusinessSuccess.type,
      businessActions.updateBusinessBump.type,
      businessActions.getBusinessSuccess.type,
    ],
    refreshReportConfigurationDefaultsAfterBusinessChange,
  );
}

export default [getReportConfigurationDefaultsActionWatcher, businessChangeReportConfigurationDefaultsWatcher];
